import React, { useMemo, useState } from 'react';
import { Field, Text, Area, File, RepeatList, useEmit } from './fields';
import { v3Api } from '../../../hooks/useApi';

const blank = () => ({
    VehicleName: '', PlateNumber: '', DriverName: '',
    LicenseAttachment: '', EmiratesIDAttachment: '',
    MaterialDetails: '', Date: '', TimeSlot: '', AdditionalInfo: '',
});

// 24 hourly slots: "12:00 AM - 01:00 AM" … (matches V2 labels).
const SLOTS = Array.from({ length: 24 }, (_, i) => {
    const fmt = (h) => {
        const suffix = h >= 12 ? 'PM' : 'AM';
        const hr = h % 12 === 0 ? 12 : h % 12;
        return `${String(hr).padStart(2, '0')}:00 ${suffix}`;
    };
    const endLabel = ((i + 1) % 24) === 23 ? '11:59 PM' : fmt((i + 1) % 24);
    return `${fmt(i)} - ${endLabel}`;
});

/** Emits an array of vehicle objects. Time slots already booked for a date are disabled. */
const VehicleDetails = ({ onDataChange }) => {
    const [rows, setRows] = useState([blank()]);
    const [bookedSlots, setBookedSlots] = useState([]);
    const data = useMemo(() => rows, [rows]);
    useEmit(data, onDataChange);

    const set = (i, key, val) => setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
    const add = () => setRows((prev) => [...prev, blank()]);
    const remove = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));
    const clear = (i) => setRows((prev) => prev.map((r, idx) => idx === i ? blank() : r));

    const onDateChange = async (i, val) => {
        set(i, 'Date', val);
        if (!val) return;
        try {
            const res = await v3Api.workPermit.bookedSlots(val);
            setBookedSlots(Array.isArray(res.bookedSlots) ? res.bookedSlots : []);
        } catch (_) { setBookedSlots([]); }
    };

    return (
        <div className="v3-wp-card">
            <div className="v3-wp-card-head"><h6 className="v3-wp-card-title">Vehicle Details</h6></div>
            <RepeatList
                title="Vehicle" items={rows} onAdd={add} onRemove={remove} onClear={clear} addLabel="Add Vehicle"
                renderRow={(v, i) => (
                    <>
                        <div className="v3-wp-grid cols-3">
                            <Field label="Vehicle Name" required><Text value={v.VehicleName} onChange={(val) => set(i, 'VehicleName', val)} /></Field>
                            <Field label="Plate Number" required><Text value={v.PlateNumber} onChange={(val) => set(i, 'PlateNumber', val)} /></Field>
                            <Field label="Driver Name" required><Text value={v.DriverName} onChange={(val) => set(i, 'DriverName', val)} /></Field>
                        </div>
                        <div className="v3-wp-grid">
                            <Field label="Upload License (PDF)" required><File accept="application/pdf" onChange={(f) => set(i, 'LicenseAttachment', f ? f.name : '')} /></Field>
                            <Field label="Upload Emirates ID (PDF)" required><File accept="application/pdf" onChange={(f) => set(i, 'EmiratesIDAttachment', f ? f.name : '')} /></Field>
                            <Field label="Date" required><Text type="date" value={v.Date} onChange={(val) => onDateChange(i, val)} /></Field>
                            <Field label="Time Slot" required>
                                <select className="v3-wp-select" value={v.TimeSlot} disabled={!v.Date} onChange={(e) => set(i, 'TimeSlot', e.target.value)}>
                                    <option value="">Select time slot</option>
                                    {SLOTS.map((s) => (
                                        <option key={s} value={s} disabled={bookedSlots.includes(s)}>
                                            {s}{bookedSlots.includes(s) ? ' (booked)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        </div>
                        <div className="v3-wp-grid">
                            <Field label="Material Details" full><Area value={v.MaterialDetails} onChange={(val) => set(i, 'MaterialDetails', val)} rows={2} /></Field>
                            <Field label="Additional Information" full><Area value={v.AdditionalInfo} onChange={(val) => set(i, 'AdditionalInfo', val)} rows={2} /></Field>
                        </div>
                    </>
                )}
            />
        </div>
    );
};

export default VehicleDetails;
